import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import './ColoradoFormPdfEditor.css';

// Served as a plain static file from public/ (copied from node_modules/pdfjs-dist/build/
// pdf.worker.min.mjs) rather than resolved via `new URL(..., import.meta.url)` — that
// bundler-asset-resolution pattern proved fragile in production: it produced a worker that
// tried to pull in additional Babel ESM helper chunks as separate runtime-resolved modules,
// which nginx's SPA fallback (try_files ... /index.html) turned into 200-with-text/html
// responses instead of real 404s, so the browser rejected them as invalid module scripts.
// A plain unprocessed static file avoids all of that — it's self-contained, exactly as
// pdf.js ships it, at a fixed path with no webpack-injected import rewriting.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

// Fallback scale only — used before the container has a measured width (or if it's ever 0).
// The real scale is computed per-render from the container's actual width (see
// computeRenderScale) so the page fits whatever screen it's displayed on, phone or desktop,
// instead of a fixed desktop-oriented pixel width that only happened to look right on one
// specific screen size. The canvas backing store is additionally multiplied by the device
// pixel ratio (below) so text stays crisp on high-DPI / zoomed displays.
const FALLBACK_RENDER_SCALE = 1.5;
const PAGE_HORIZONTAL_PADDING = 24; // keeps the fitted page off the container's edges
const MIN_PINCH_ZOOM = 1;   // never smaller than the fit-to-width render
const MAX_PINCH_ZOOM = 4;

/**
 * In-browser editor for a Colorado CREC form.
 *
 * Loads the EDITABLE (unflattened) copy from FormService, renders each page to a canvas, and
 * overlays real HTML inputs positioned exactly over the PDF's AcroForm widgets. The user edits the
 * form on top of the actual document; on Save we PATCH only the changed fields to the overrides
 * endpoint, which regenerates both the edit/ and view/ copies server-side.
 *
 * Props:
 *   doc      { packageId, instanceId, name }
 *   getToken () => Promise<string>   Firebase ID token provider (page-local, per this app's pattern)
 *   onClose  () => void
 *   onSaved  () => void              called after a successful save (parent refetches the list)
 */
export default function ColoradoFormPdfEditor({ doc, getToken, onClose, onSaved }) {
  const { packageId, instanceId, name } = doc || {};
  const containerRef = useRef(null);
  const bodyRef = useRef(null);
  const pdfRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pageCount, setPageCount] = useState(0);

  // fieldName -> current string value (checkbox uses its export/on value or 'Off')
  const valuesRef = useRef({});
  // fieldName -> true once the user changes it (only dirty fields are saved as overrides)
  const dirtyRef = useRef({});
  const [dirtyCount, setDirtyCount] = useState(0);

  // Pinch-zoom state. `.cfe-body` (bodyRef) is the fixed viewport; `.cfe-pages` (containerRef) is
  // the element we actually transform. Both live entirely inside the modal body — the header and
  // footer are separate flex siblings of `.cfe-body` in the JSX below, so scoping the zoom to
  // containerRef structurally cannot touch them.
  //
  // This uses `transform: translate() scale()`, NOT the CSS `zoom` property (tried first, reverted
  // — see git history). `zoom` reflows layout on every pinch tick, and reflowing content above the
  // currently-scrolled position silently drags `.cfe-body`'s scroll position along with it, so the
  // page visibly scrolled out from under the user's fingers mid-pinch. `transform` never reflows,
  // so position has to be tracked and clamped manually (naturalSizeRef/scaleRef/txRef/tyRef below),
  // but in exchange the pinch can be anchored exactly to the content point under the fingers with
  // no drift.
  const naturalSizeRef = useRef({ width: 0, height: 0 }); // .cfe-pages' own size at scale 1
  const scaleRef = useRef(1);
  const txRef = useRef(0);
  const tyRef = useRef(0);
  const gestureRef = useRef(null); // { type: 'pinch' | 'pan', ... } while a touch gesture is live

  const markDirty = useCallback((fieldName, value) => {
    valuesRef.current[fieldName] = value;
    if (!dirtyRef.current[fieldName]) {
      dirtyRef.current[fieldName] = true;
      setDirtyCount((c) => c + 1);
    }
  }, []);

  // Build the overlay inputs for one page's widget annotations. Prefers whatever the user has
  // already typed (valuesRef) over the PDF's own stored value, so this stays correct even if a
  // page is ever re-mounted after the user has started editing it.
  const mountFieldInputs = useCallback((annotations, viewport, overlay) => {
    annotations
      .filter((a) => a.subtype === 'Widget' && a.fieldName && !a.readOnly && !a.pushButton)
      .forEach((a) => {
        const r = viewport.convertToViewportRectangle(a.rect);
        const left = Math.min(r[0], r[2]);
        const top = Math.min(r[1], r[3]);
        const width = Math.abs(r[2] - r[0]);
        const height = Math.abs(r[3] - r[1]);
        const existing = valuesRef.current[a.fieldName];

        let el;
        if (a.fieldType === 'Btn' && a.checkBox) {
          el = document.createElement('input');
          el.type = 'checkbox';
          const onVal = a.exportValue || 'On';
          const isOn = existing != null ? existing !== 'Off' : (a.fieldValue && a.fieldValue !== 'Off');
          if (isOn) valuesRef.current[a.fieldName] = onVal;
          el.checked = !!isOn;
          el.addEventListener('change', () => markDirty(a.fieldName, el.checked ? onVal : 'Off'));
        } else if (a.fieldType === 'Ch' && Array.isArray(a.options) && a.options.length) {
          el = document.createElement('select');
          const blank = document.createElement('option');
          blank.value = ''; blank.textContent = '—';
          el.appendChild(blank);
          a.options.forEach((opt) => {
            const o = document.createElement('option');
            o.value = opt.exportValue ?? opt.displayValue;
            o.textContent = opt.displayValue ?? opt.exportValue;
            el.appendChild(o);
          });
          const v = existing != null ? existing : a.fieldValue;
          if (v != null) { el.value = v; valuesRef.current[a.fieldName] = v; }
          el.addEventListener('change', () => markDirty(a.fieldName, el.value));
        } else if (a.fieldType === 'Tx') {
          const multiline = !!a.multiLine;
          el = document.createElement(multiline ? 'textarea' : 'input');
          if (!multiline) el.type = 'text';
          const v = existing != null ? existing : (a.fieldValue != null ? String(a.fieldValue) : '');
          el.value = v;
          if (v) valuesRef.current[a.fieldName] = v;
          el.addEventListener('input', () => markDirty(a.fieldName, el.value));
        } else {
          return; // radio groups / unsupported — skip for now
        }

        el.className = 'cfe-field';
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
        // Scaled off this field's OWN rendered box height, not a fixed size — a page can render
        // small (e.g. a low fit-to-width scale on a small phone) or large, and a flat font-size
        // looked comically oversized on small fields on small renders. Ratio tuned against the
        // CSS fallback (12px) that used to sit on a ~20px-tall field at the old fixed 1.5 scale.
        el.style.fontSize = `${Math.max(7, Math.min(16, height * 0.55))}px`;
        if (a.fieldType === 'Btn') { el.style.width = `${Math.min(width, height)}px`; el.style.height = el.style.width; }
        el.title = a.fieldName;
        // Center whatever field the user taps into — on a phone the fitted page can still be
        // small enough that it's unclear exactly which field has focus without this.
        el.addEventListener('focus', () => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        });
        overlay.appendChild(el);
      });
  }, [markDirty]);

  // Renders one page's canvas + field overlay at the fit-to-width scale into a page wrapper
  // element. Zooming in/out afterwards is handled separately (see the pinch-zoom effect below) by
  // CSS-transforming the already-rendered DOM — pages are never re-rendered at a different scale,
  // so this only ever runs once per page per document load.
  const renderPageInto = useCallback(async (pdf, pageNum, scale, pageWrap) => {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale });
    const dpr = window.devicePixelRatio || 1;

    pageWrap.style.width = `${viewport.width}px`;
    pageWrap.style.height = `${viewport.height}px`;
    pageWrap.innerHTML = '';

    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width * dpr);
    canvas.height = Math.floor(viewport.height * dpr);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    canvas.className = 'cfe-canvas';
    pageWrap.appendChild(canvas);

    const overlay = document.createElement('div');
    overlay.className = 'cfe-overlay';
    pageWrap.appendChild(overlay);

    await page.render({
      canvasContext: canvas.getContext('2d'),
      viewport,
      transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
      // Render only the static form background — NOT the AcroForm field appearances. The
      // editable copy is unflattened, so its field values live in the widgets; our HTML input
      // overlays display those values. Painting them on the canvas too would double the text.
      annotationMode: pdfjsLib.AnnotationMode.DISABLE,
    }).promise;
    const annotations = await page.getAnnotations();
    mountFieldInputs(annotations, viewport, overlay);
  }, [mountFieldInputs]);

  // Two-finger pinch + pan scoped to `.cfe-body`. Native WebView/browser pinch-zoom is turned off
  // entirely (mobile: setBuiltInZoomControls={false} + a fixed viewport meta; desktop browser:
  // pinch-zoom isn't a touch gesture there) — this replaces it with our own gesture handling so
  // the zoom can be confined to the scrollable pages area instead of the whole embedded page.
  //
  // At scale 1 this does nothing at all — `.cfe-body` scrolls natively exactly as before, so
  // normal page-to-page scrolling is untouched. A pinch hands vertical position over from native
  // scroll to our own transform (preserving the exact scroll offset, so nothing jumps), and stays
  // in that mode — panning with one or two fingers — until the user pinches back down to ~1x, at
  // which point position is handed back to native scroll and the transform is dropped.
  useEffect(() => {
    const body = bodyRef.current;
    const container = containerRef.current;
    if (!body || !container) return undefined;

    const distanceBetween = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };
    const midpoint = (touches) => {
      const rect = body.getBoundingClientRect();
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
        y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top,
      };
    };

    // Keeps zoomed-in content from panning past its own edges; centers it when it's narrower
    // than the viewport (never happens for width in practice — pages fit the container width at
    // scale 1 and only grow from there — but cheap to handle correctly).
    const clampTranslate = (tx, ty, scale) => {
      const { width: nw, height: nh } = naturalSizeRef.current;
      const vw = body.clientWidth;
      const vh = body.clientHeight;
      const cw = nw * scale;
      const ch = nh * scale;
      const clampedTx = cw <= vw ? (vw - cw) / 2 : Math.min(0, Math.max(vw - cw, tx));
      const clampedTy = ch <= vh ? (vh - ch) / 2 : Math.min(0, Math.max(vh - ch, ty));
      return { tx: clampedTx, ty: clampedTy };
    };

    const applyTransform = () => {
      const { tx, ty } = clampTranslate(txRef.current, tyRef.current, scaleRef.current);
      txRef.current = tx;
      tyRef.current = ty;
      container.style.transform = `translate(${tx}px, ${ty}px) scale(${scaleRef.current})`;
    };

    const enterZoomMode = () => {
      if (body.style.overflow === 'hidden') return; // already in zoomed mode
      tyRef.current = -body.scrollTop;
      txRef.current = 0;
      body.style.overflow = 'hidden'; // both axes — we own panning entirely from here on
      applyTransform();
    };

    const exitZoomMode = () => {
      body.scrollTop = -tyRef.current;
      body.style.overflow = ''; // falls back to the CSS `overflow: auto` rule
      container.style.transform = '';
      scaleRef.current = 1;
      txRef.current = 0;
      tyRef.current = 0;
    };

    const onTouchStart = (e) => {
      if (naturalSizeRef.current.width <= 0) return; // not measured yet (still loading)
      if (e.touches.length === 2) {
        if (scaleRef.current <= 1.001) enterZoomMode();
        gestureRef.current = {
          type: 'pinch',
          startDistance: distanceBetween(e.touches),
          startScale: scaleRef.current,
        };
      } else if (e.touches.length === 1 && scaleRef.current > 1.001) {
        // Single-finger drag is only ours to handle once already zoomed in — at scale 1 this is
        // left completely alone so ordinary native page scrolling behaves exactly as before.
        gestureRef.current = {
          type: 'pan',
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startTx: txRef.current,
          startTy: tyRef.current,
        };
      }
    };

    const onTouchMove = (e) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      if (gesture.type === 'pinch' && e.touches.length === 2) {
        e.preventDefault();
        const mid = midpoint(e.touches);
        const ratio = distanceBetween(e.touches) / gesture.startDistance;
        const newScale = Math.min(MAX_PINCH_ZOOM, Math.max(MIN_PINCH_ZOOM, gesture.startScale * ratio));
        // Anchor to whatever content point is currently under the fingers' midpoint: read it
        // using the pre-update tx/ty/scale, then solve the tx/ty that puts that same content
        // point back under the (possibly also-moved) midpoint at the new scale. This is what
        // stops the page from drifting/scrolling out from under the user's fingers mid-pinch.
        const contentX = (mid.x - txRef.current) / scaleRef.current;
        const contentY = (mid.y - tyRef.current) / scaleRef.current;
        scaleRef.current = newScale;
        txRef.current = mid.x - contentX * newScale;
        tyRef.current = mid.y - contentY * newScale;
        applyTransform();
      } else if (gesture.type === 'pan' && e.touches.length === 1) {
        e.preventDefault();
        const dx = e.touches[0].clientX - gesture.startX;
        const dy = e.touches[0].clientY - gesture.startY;
        txRef.current = gesture.startTx + dx;
        tyRef.current = gesture.startTy + dy;
        applyTransform();
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length >= 2) return; // still pinching with the remaining fingers
      if (e.touches.length === 1 && gestureRef.current?.type === 'pinch') {
        // Dropped from two fingers to one mid-gesture — hand off into a pan from here instead of
        // stopping abruptly.
        gestureRef.current = {
          type: 'pan',
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startTx: txRef.current,
          startTy: tyRef.current,
        };
        return;
      }
      gestureRef.current = null;
      // Guard on actually being in zoom mode (overflow:hidden, set by enterZoomMode), not just on
      // scale — scale is 1 on EVERY plain tap, zoomed or not, so checking scale alone made
      // exitZoomMode() fire on every single tap and force `body.scrollTop = 0`, resetting the
      // scroll position to the top of the document on any tap at all, whether or not the user had
      // ever zoomed.
      if (e.touches.length === 0 && scaleRef.current <= 1.001 && body.style.overflow === 'hidden') {
        exitZoomMode();
      }
    };

    body.addEventListener('touchstart', onTouchStart, { passive: true });
    body.addEventListener('touchmove', onTouchMove, { passive: false });
    body.addEventListener('touchend', onTouchEnd, { passive: true });
    body.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      body.removeEventListener('touchstart', onTouchStart);
      body.removeEventListener('touchmove', onTouchMove);
      body.removeEventListener('touchend', onTouchEnd);
      body.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  // ── Load + render ───────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const resp = await apiClient.get(
          API_ENDPOINTS.formsEditablePdf(packageId, instanceId),
          { headers: { Authorization: `Bearer ${token}` }, responseType: 'arraybuffer' },
        );
        if (cancelled) return;

        const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(resp.data) }).promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.numPages);

        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = '';
        container.style.transform = '';
        if (bodyRef.current) bodyRef.current.style.overflow = '';
        scaleRef.current = 1;
        txRef.current = 0;
        tyRef.current = 0;
        gestureRef.current = null;

        // Fit the page to the container's actual width instead of a fixed pixel size — the
        // same computed scale is reused for every page (CO forms don't mix page sizes), and
        // for field positions via viewport.convertToViewportRectangle, so canvas rendering and
        // the overlaid input fields always agree on where things are.
        const firstPage = await pdf.getPage(1);
        const nativeWidth = firstPage.getViewport({ scale: 1 }).width;
        const availableWidth = (container.clientWidth || 0) - PAGE_HORIZONTAL_PADDING * 2;
        const renderScale = availableWidth > 0 && nativeWidth > 0
          ? Math.max(0.3, Math.min(availableWidth / nativeWidth, 2.5))
          : FALLBACK_RENDER_SCALE;

        for (let n = 1; n <= pdf.numPages; n++) {
          if (cancelled) return;
          const pageWrap = document.createElement('div');
          pageWrap.className = 'cfe-page';
          container.appendChild(pageWrap);
          await renderPageInto(pdf, n, renderScale, pageWrap);
        }
        if (!cancelled) {
          // Measured once, right after render, while the transform is still identity — pinch/pan
          // math (see the touch-handling effect) treats this as fixed for the life of the loaded
          // document, since pages never reflow after this point (zoom is transform-only).
          naturalSizeRef.current = { width: container.offsetWidth, height: container.offsetHeight };
        }
      } catch (e) {
        console.error('[ColoradoFormPdfEditor] load failed:', e);
        if (!cancelled) {
          const msg = e?.response?.status === 409 || e?.response?.status === 400
            ? 'This form can no longer be edited (it may be signed).'
            : 'Could not open the form for editing. Please try again.';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId, instanceId]);

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const overrides = {};
    Object.keys(dirtyRef.current).forEach((k) => { overrides[k] = valuesRef.current[k] ?? ''; });
    if (Object.keys(overrides).length === 0) { onClose(); return; }

    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await apiClient.patch(
        API_ENDPOINTS.formsApplyOverrides(packageId, instanceId),
        { overrides },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      onSaved && onSaved();
      onClose();
    } catch (e) {
      console.error('[ColoradoFormPdfEditor] save failed:', e);
      setError(e?.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cfe-backdrop" onClick={() => !saving && onClose()}>
      <div className="cfe-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cfe-header">
          <div>
            <div className="cfe-title">Edit Form — {name}</div>
            <div className="cfe-sub">
              {loading ? 'Loading…'
                : `${pageCount} page${pageCount === 1 ? '' : 's'} · ${dirtyCount} field${dirtyCount === 1 ? '' : 's'} changed`}
            </div>
          </div>
          <button className="cfe-x" onClick={() => !saving && onClose()} aria-label="Close">×</button>
        </div>

        <div ref={bodyRef} className="cfe-body">
          {error && <div className="cfe-error">{error}</div>}
          {loading && <div className="cfe-loading"><span className="cfe-spinner" />Opening the form…</div>}
          <div ref={containerRef} className="cfe-pages" />
        </div>

        <div className="cfe-footer">
          <button className="cfe-btn" onClick={() => !saving && onClose()} disabled={saving}>Cancel</button>
          <button className="cfe-btn cfe-btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving…' : dirtyCount > 0 ? `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
